import { castArray } from 'lodash';
import {
  ApiResponse,
  CCApiResponse,
  OutageStrategyEnum,
  PriceIntegrationSourceEnum,
  productPriceCurrencyPositionEnum,
  ProjectConfigInterface,
  ProjectConfigSchema,
} from 'src/interfaces';

export const mapProjectConfigs = (
  response: CCApiResponse,
  configKey?: keyof ProjectConfigInterface,
): Partial<ProjectConfigInterface> => {
  if (!configKey) {
    for (const key of Object.keys(ProjectConfigSchema.properties)) {
      const keyExisted = response.data.some(
        (item: ApiResponse) => item.config_key === key,
      );

      if (!keyExisted) {
        response.data.push({ config_key: key });
      }
    }
  }

  return castArray(response.data).reduce(
    (acc: Partial<ProjectConfigInterface>, item: ApiResponse) => {
      const formatConfig = mapProjectConfig(item);

      return {
        ...acc,
        ...formatConfig,
      };
    },
    {} as Partial<ProjectConfigInterface>,
  );
};

const mapProjectConfig = (
  response: ApiResponse,
): Partial<ProjectConfigInterface> => {
  const { config_key, value } = response;

  switch (config_key as keyof ProjectConfigInterface) {
    case 'productPriceDecimalSeparator':
    case 'productPriceCalorieSeparator':
    case 'productPriceSeparator':
    case 'productPriceCurrency':
    case 'productNameField':
    case 'codPosition':
    case 'productPriceSize':
    case 'globalComponents': {
      return { [config_key]: value ?? '' };
    }
    case 'showZeroPrices':
    case 'currencySpace':
    case 'productFullPriceOnly':
    case 'autoscale': {
      return { [config_key]: value ?? false };
    }
    case 'gcf':
    case 'showGlobalProductDisclaimer':
    case 'globalComponentsScreens': {
      return { [config_key]: value ?? {} };
    }
    case 'fonts': {
      return value?.length ? { [config_key]: value } : {};
    }
    case 'productPriceCurrencyPosition': {
      return { [config_key]: value ?? productPriceCurrencyPositionEnum.before };
    }
    case 'priceIntegrationSource': {
      return { [config_key]: value ?? PriceIntegrationSourceEnum.PRICE_POSTER };
    }
    case 'outageStrategy': {
      return { [config_key]: value ?? OutageStrategyEnum.Soldout };
    }
    case 'productPriceSoldoutSticker': {
      return {
        [config_key]: value ?? {
          backgroundColor: '',
          textColor: '',
          textValue: '',
        },
      };
    }
    case 'nutrition': {
      return { [config_key]: value ?? { active: false, label: '' } };
    }
    case 'rbStoreIntegration': {
      return { [config_key]: value.enabled ?? false };
    }
    case 'themes': {
      return {
        [config_key]: value ?? {
          DT: {
            padding: 0,
            margin: 0,
            subproducts: {
              label: {
                border: '1px solid #FFFFFF',
              },
            },
          },
          DTLISTING: {
            padding: 0,
            margin: 0,
            subproducts: {
              label: {
                border: '1px solid #FFFFFF',
              },
            },
          },
          FC: {
            padding: 0,
            margin: 0,
            subproducts: {
              label: {
                border: '1px solid #FFFFFF',
              },
            },
          },
          FC_DARK_MODE: {
            padding: 0,
            margin: 0,
            subproducts: {
              label: {
                border: '1px solid #FFFFFF',
              },
            },
          },
          DT_DARK_MODE: {
            padding: 0,
            margin: 0,
            subproducts: {
              label: {
                border: '1px solid #FFFFFF',
              },
            },
          },
          DTLISTING_DARK_MODE: {
            padding: 0,
            margin: 0,
            subproducts: {
              label: {
                border: '1px solid #FFFFFF',
              },
            },
          },
        },
      };
    }
    case 'activeTheme': {
      return { [config_key]: value ?? 'DT' };
    }
    case 'indoorDefaultTheme': {
      return { [config_key]: value ?? 'FC' };
    }
    case 'outdoorDefaultTheme': {
      return { [config_key]: value ?? 'DT' };
    }
    case 'mcCafeDefaultTheme': {
      return { [config_key]: value ?? 'FC' };
    }
    case 'digitalMerchandiserDefaultTheme': {
      return { [config_key]: value ?? 'FC' };
    }
    case 'customizedOutageConfig': {
      return {
        [config_key]: value ?? {
          textValue: '',
          stamp: {
            visible: false,
            url: '',
          },
        },
      };
    }
    default:
      return { [config_key]: value ?? {} };
  }
};
